// adminAgent.ts — Actor factory for the dominus_admin canister.
//
// Follows the exact pattern established in src/lib/icpAgent.ts.
// Types mirror the Motoko public types in canisters/admin/main.mo verbatim.
//
// Security: fetchRootKey() is called ONLY in dev mode. (CLAUDE.md Anti-Hallucination #10)
//
// NOTE: Admin canister ID is read from VITE_ADMIN_CANISTER_ID env var.
//       Set it in sites/admin/.env.local after dfx deploy.

import { Actor, HttpAgent, type ActorSubclass, type Identity } from '@dfinity/agent';
import type { Principal } from '@dfinity/principal';
import type { InterfaceFactory } from '@dfinity/candid/lib/cjs/idl';

// ---------------------------------------------------------------------------
// Types mirrored from canisters/admin/main.mo (public types section)
// ---------------------------------------------------------------------------

export interface CanisterHealthStatus {
  cycles: bigint;
  heapBytes: bigint;
  heapPercent: bigint;
  memoryBytes: bigint;
  memoryPercent: bigint;
  timestamp: bigint;
}

/// On-the-wire field is `label` (not `label_`) — Motoko emits its `label_`
/// escape of the reserved word back to `label` in the generated Candid.
/// The frontend IDL must match the wire format, not the Motoko source name.
/// (Mismatch produced "Cannot find required field label_" decode errors —
///  L-2 fallout, fixed 2026-05-22.)
export interface CanisterRollup {
  label: string;
  principal: { toText(): string };
  health: CanisterHealthStatus;
}

export interface AdminAction {
  timestamp: bigint;
  caller: { toText(): string };
  actionType: string;
  targetPrincipal: [] | [{ toText(): string }];
  details: string;
}

export interface TopUpSummary {
  triggered: bigint;
  cyclesDeposited: bigint;
  capRemaining: bigint;
  skipped: string[];
  timestamp: bigint;
}

export interface AuthMetrics {
  totalProfiles: bigint;
  profilesCreatedLast24h: bigint;
  profilesCreatedLast7d: bigint;
  friendCodesClaimed: bigint;
  friendCodesUnclaimed: bigint;
}

export interface LobbyMetrics {
  totalRoomsCreated: bigint;
  activePublicRooms: bigint;
  activePrivateRooms: bigint;
  roomsCreatedLast24h: bigint;
  oldestActiveRoomAgeMs: bigint;
}

export interface GameMetrics {
  totalGamesPlayed: bigint;
  gamesInProgress: bigint;
  gamesCompletedLast24h: bigint;
  gamesCompletedLast7d: bigint;
  avgGameDurationMs: bigint;
  mostPopularTarget: bigint;
  mostPopularMode: string;
  aiVsMultiplayerRatio: bigint;
}

export interface SubscriptionMetrics {
  activeSubscriptions: bigint;
  mrrCents: bigint;
  signupsLast24h: bigint;
  signupsLast7d: bigint;
  signupsLast30d: bigint;
  // Trial visibility (additive). Mirrors canisters/admin/main.mo
  // SubscriptionMetrics and canisters/subscription/main.mo admin_get_metrics —
  // same names, Nat types, and ordering so Candid structural subtyping keeps
  // these fields instead of silently dropping them.
  activeTrials: bigint;
  trialsStartedLast24h: bigint;
  trialsStartedLast7d: bigint;
  churnLast30d: bigint;
  failedPaymentsAwaitingRetry: bigint;
  refundsLast30d: bigint;
  disputesOpen: bigint;
}

export interface AggregatedMetrics {
  auth: [] | [AuthMetrics];
  lobby: [] | [LobbyMetrics];
  game: [] | [GameMetrics];
  subscription: [] | [SubscriptionMetrics];
  fetchedAt: bigint;
}

export interface AutoTopUpConfig {
  enabled: boolean;
  threshold: bigint;
  amount: bigint;
  dailyCap: bigint;
  dailyUsed: bigint;
  dailyWindowStart: bigint;
  capRemaining: bigint;
}

// Subscription override variant -- Candid variant maps to a tagged record on
// the JS side. Exactly one branch must be present per call.
//   { deactivate: <reason: string> }  -> #deactivate(Text) on the canister
//   { extend: <days: bigint> }        -> #extend(Nat) on the canister
export type SubscriptionOverrideAction =
  | { deactivate: string }
  | { extend: bigint };

// Result type mirrors Motoko's Result.Result<T, Text>
export type OkResult<T> = { ok: T };
export type ErrResult = { err: string };
export type AdminResult<T> = OkResult<T> | ErrResult;

// ---------------------------------------------------------------------------
// Session token types — mirrored from Koda's Phase 3 canister additions
// ---------------------------------------------------------------------------

export interface SessionToken {
  token: string;
  expiresAt: bigint; // nanoseconds since Unix epoch (convert via Number(v/1_000_000n) for JS ms)
}

// ---------------------------------------------------------------------------
// Data Rights types — mirrored from Koda's canisters/admin/main.mo additions
// ---------------------------------------------------------------------------

export interface ExportedUserProfile {
  principal: Principal;
  displayName: string;
  createdAt: bigint;
  gamesPlayed: bigint;
  gamesWon: bigint;
}

export interface ExportedSubscription {
  isActive: boolean;
  activatedAt: bigint;
  expiresAt: bigint;
  stripeCustomerId: string;
  trialStartedAt: [] | [bigint];
  trialEndsAt: [] | [bigint];
  seatNumber: [] | [bigint];
  isFoundingHand: boolean;
}

export interface PlayerStatsExport {
  schemaVersion: bigint;
  matchesPlayed: bigint; matchesWon: bigint;
  matchesWonByDominoOut: bigint; matchesWonByBlock: bigint;
  currentMatchStreak: bigint; bestMatchStreak: bigint;
  matchesPlayedDraw: bigint; matchesWonDraw: bigint;
  matchesPlayedStraight: bigint; matchesWonStraight: bigint;
  matchesPlayedByTarget100: bigint; matchesPlayedByTarget200: bigint; matchesPlayedByTarget300: bigint;
  matchesWonByTarget100: bigint; matchesWonByTarget200: bigint; matchesWonByTarget300: bigint;
  roundsPlayed: bigint; roundsWon: bigint;
  roundsWonByDominoOut: bigint; roundsWonByBlock: bigint; roundsBlocked: bigint;
  totalPointsScored: bigint; highestRoundScore: bigint; highestMatchScore: bigint;
  totalPointsScoredDraw: bigint; totalPointsScoredStraight: bigint;
  pointsScoredByDominoOut: bigint; pointsScoredByBlock: bigint;
  totalTilesPlayed: bigint; totalPasses: bigint; totalBoneyardDraws: bigint;
  dominoOuts: bigint; totalTilesRemainingOnLoss: bigint; roundsLostCount: bigint;
  totalPlayTimeMs: bigint; totalTurnTimeMs: bigint; turnsPlayed: bigint;
  fastestMatchWinMs: bigint; longestMatchMs: bigint;
  firstGameDate: [] | [bigint]; lastGameDate: [] | [bigint];
  totalSessions: bigint;
  roundsWonWithoutPass: bigint; totalOpponentPipsOnWin: bigint;
  timedOutTurns: bigint; roundsClosedWithLastTile: bigint; longestOwnPlaySequence: bigint;
  migrated: boolean; lastMatchAt: [] | [bigint];
}

export interface UserDataBundle {
  exportedAt: bigint;
  exporterPrincipal: Principal;
  targetPrincipal: Principal;
  profile: [] | [ExportedUserProfile];
  subscription: [] | [ExportedSubscription];
  stats: [] | [PlayerStatsExport];
}

// ---------------------------------------------------------------------------
// Stale-rooms + cycles dashboard types — mirrored from D2/D3 canister methods
// (canisters/admin/main.mo:1651, :1721, :1594) shipped 2026-05-11.
//
// These types match the Motoko structural subtype declared at
// canisters/admin/main.mo:649-664. The fields list_stale_rooms reads
// (status, createdAt, gameStartedAt) MUST remain present in lobby's
// RoomInfo for filtering to remain correct -- the admin canister itself
// declares these as structural subtypes and Candid will drop any extra
// fields lobby adds silently.
// ---------------------------------------------------------------------------

export type LobbyRoomStatus =
  | { waiting: null }
  | { full: null }
  | { playing_open: null }
  | { playing: null }
  | { finished: null };

export type LobbyGameMode = { draw: null } | { straight: null };

export type LobbyRoomVisibility =
  | { public_room: null }
  | { private_room: null };

export interface LobbyRoomInfo {
  roomId: string;
  mode: LobbyGameMode;
  target: bigint;
  hostPrincipal: Principal;
  hostName: string;
  playerCount: bigint;
  maxPlayers: bigint;
  createdAt: bigint;
  status: LobbyRoomStatus;
  playerNames: string[];
  playerPrincipals: Principal[];
  visibility: LobbyRoomVisibility;
  gameRoomId: [] | [string];
  gameStartedAt: [] | [bigint];
}

/// One (canisterLabel, cycleBalance) tuple per known canister.
/// "Skipped" entries (principal not yet wired) carry a label suffix
/// " (not wired)" + cycles=0. Offline canisters carry " (status failed)" + 0.
export type CanisterCyclesEntry = readonly [string, bigint];

/// Backend principal directory returned by get_backend_principals.
/// Matches the record produced at canisters/admin/main.mo:1594 -- each
/// entry is `?Principal` because principals are nullable in the canister
/// (they may not yet be wired via set_*_canister_principal).
export interface BackendPrincipals {
  auth: [] | [Principal];
  lobby: [] | [Principal];
  game: [] | [Principal];
  subscription: [] | [Principal];
  cyclesWallet: [] | [Principal];
  stats: [] | [Principal];
  frontend: [] | [Principal];
}

// The admin canister service interface
export interface AdminService {
  // Admin management
  init_admin: () => Promise<AdminResult<null>>;
  add_admin: (principal: { toText(): string }) => Promise<AdminResult<null>>;
  remove_admin: (principal: { toText(): string }) => Promise<AdminResult<null>>;
  get_admins: () => Promise<{ toText(): string }[]>;
  is_admin: (principal: { toText(): string }) => Promise<boolean>;

  // Metrics
  admin_get_aggregated_metrics: () => Promise<AdminResult<AggregatedMetrics>>;
  admin_get_all_canister_health: () => Promise<AdminResult<CanisterRollup[]>>;

  // Auto top-up config read
  get_auto_top_up_config: () => Promise<AutoTopUpConfig>;

  // Auto top-up
  auto_top_up_check: () => Promise<AdminResult<TopUpSummary>>;
  set_auto_top_up_enabled: (enabled: boolean) => Promise<AdminResult<null>>;
  set_auto_top_up_threshold: (threshold: bigint) => Promise<AdminResult<null>>;
  set_auto_top_up_amount: (amount: bigint) => Promise<AdminResult<null>>;
  set_daily_top_up_cap: (cap: bigint) => Promise<AdminResult<null>>;

  // Banned words
  admin_list_banned_words: () => Promise<string[]>;
  admin_add_banned_word: (word: string) => Promise<AdminResult<null>>;
  admin_remove_banned_word: (word: string) => Promise<AdminResult<null>>;
  admin_set_banned_words: (words: string[]) => Promise<AdminResult<null>>;

  // Audit log — 3 positional args matching Motoko signature
  admin_get_audit_log_filtered: (
    actionType: [] | [string],
    sinceTimestamp: [] | [bigint],
    limit: bigint,
  ) => Promise<AdminResult<AdminAction[]>>;

  // Manual actions
  admin_force_rename: (target: { toText(): string }, newName: string) => Promise<AdminResult<null>>;
  admin_force_finish_room: (roomIdOrCode: string) => Promise<AdminResult<null>>;

  // Manual cycle top-up. Accepts a Principal (admin frontend resolves the
  // canister-name string from `get_backend_principals` before calling).
  // Returns the cycles actually deposited on success.
  admin_manual_top_up_canister: (
    target: { toText(): string },
    amount: bigint,
  ) => Promise<AdminResult<bigint>>;

  // Subscription override -- single endpoint for deactivate / extend so the
  // audit log carries one action_type per intent.
  admin_subscription_override: (
    target: { toText(): string },
    action: SubscriptionOverrideAction,
  ) => Promise<AdminResult<null>>;

  // Session token — Phase 3 (2026-05-14). Canister-issued tokens replace
  // the shared-secret ADMIN_API_TOKEN in interactive Worker calls.
  admin_issue_session_token: () => Promise<AdminResult<SessionToken>>;
  validate_session_token: (token: string) => Promise<[] | [Principal]>;  // Candid opt Principal
  admin_revoke_session_token: (token: string) => Promise<AdminResult<null>>;

  // Data Rights — Block 3 (2026-05-14). Koda shipped the Motoko methods;
  // these TS signatures mirror their Candid types verbatim.
  admin_export_user_data: (target: Principal) => Promise<AdminResult<UserDataBundle>>;
  admin_lookup_principal_by_stripe_customer: (stripeCustomerId: string) => Promise<AdminResult<[] | [Principal]>>;
  admin_log_privacy_event: (
    actionType: string,
    targetPrincipalText: [] | [string],
    details: string,
  ) => Promise<AdminResult<null>>;

  // Stale-rooms + cycles dashboard — D2 / D3 (canister side shipped 2026-05-11).
  // Frontend wired 2026-05-22 to drop the useStaleRooms.ts MOCK_ROOMS array.
  //
  // `list_stale_rooms(minAgeNs)` is an UPDATE (not query) per main.mo:1651
  // because it fans out to lobby_canister.admin_canister_export_rooms which
  // is itself a query but the cross-canister call must be made from an
  // update context to be auditable.
  //
  // `get_all_canister_cycles` is an UPDATE per main.mo:1721 because it calls
  // mgmt.canister_status (a state-mutating inter-canister call).
  list_stale_rooms: (minAgeNs: bigint) => Promise<AdminResult<Array<[string, LobbyRoomInfo]>>>;
  get_all_canister_cycles: () => Promise<AdminResult<CanisterCyclesEntry[]>>;
  get_backend_principals: () => Promise<BackendPrincipals>;

  // ICP -> Cycles top-up via CMC — shipped 2026-05-22 (Atlas).
  //
  // get_icp_topup_account_id: a public query (no admin gate -- the account
  // ID is deterministic per canister principal, exposing it does no harm).
  // Returns the 64-char hex AccountIdentifier that Jerry should send ICP TO
  // from his NNS account.
  //
  // notify_icp_topup: admin-gated update. Takes the block index from the
  // NNS dapp's transfer confirmation. Returns cycles minted on success or a
  // typed error message describing the CMC failure mode (Refunded,
  // InvalidTransaction, TransactionTooOld, Processing, CMC unreachable).
  get_icp_topup_account_id: () => Promise<string>;
  notify_icp_topup: (blockIndex: bigint) => Promise<AdminResult<bigint>>;
}

// ---------------------------------------------------------------------------
// Candid IDL factory for dominus_admin
//
// Hand-written to match the Motoko interface. Follows the InterfaceFactory
// calling convention used by @dfinity/agent Actor.createActor: receives
// { IDL } as its argument, builds descriptors using THAT IDL instance.
// (This avoids the module-level IDL import type mismatch with InterfaceFactory.)
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const adminIdlFactory: InterfaceFactory = ({ IDL }: any) => {
  const CanisterHealthStatus = IDL.Record({
    cycles: IDL.Nat,
    heapBytes: IDL.Nat,
    heapPercent: IDL.Nat,
    memoryBytes: IDL.Nat,
    memoryPercent: IDL.Nat,
    timestamp: IDL.Int,
  });

  // Field is `label` on the wire (Motoko's `label_` escape collapses to
  // `label` in the emitted Candid). Mismatch caused the
  // "Cannot find required field label_" decode error reported 2026-05-22.
  const CanisterRollupIDL = IDL.Record({
    label: IDL.Text,
    principal: IDL.Principal,
    health: CanisterHealthStatus,
  });

  const AdminActionIDL = IDL.Record({
    timestamp: IDL.Int,
    caller: IDL.Principal,
    actionType: IDL.Text,
    targetPrincipal: IDL.Opt(IDL.Principal),
    details: IDL.Text,
  });

  const TopUpSummaryIDL = IDL.Record({
    triggered: IDL.Nat,
    cyclesDeposited: IDL.Nat,
    capRemaining: IDL.Nat,
    skipped: IDL.Vec(IDL.Text),
    timestamp: IDL.Int,
  });

  const AuthMetricsIDL = IDL.Record({
    totalProfiles: IDL.Nat,
    profilesCreatedLast24h: IDL.Nat,
    profilesCreatedLast7d: IDL.Nat,
    friendCodesClaimed: IDL.Nat,
    friendCodesUnclaimed: IDL.Nat,
  });

  const LobbyMetricsIDL = IDL.Record({
    totalRoomsCreated: IDL.Nat,
    activePublicRooms: IDL.Nat,
    activePrivateRooms: IDL.Nat,
    roomsCreatedLast24h: IDL.Nat,
    oldestActiveRoomAgeMs: IDL.Int,
  });

  const GameMetricsIDL = IDL.Record({
    totalGamesPlayed: IDL.Nat,
    gamesInProgress: IDL.Nat,
    gamesCompletedLast24h: IDL.Nat,
    gamesCompletedLast7d: IDL.Nat,
    avgGameDurationMs: IDL.Int,
    mostPopularTarget: IDL.Nat,
    mostPopularMode: IDL.Text,
    aiVsMultiplayerRatio: IDL.Nat,
  });

  const SubscriptionMetricsIDL = IDL.Record({
    activeSubscriptions: IDL.Nat,
    mrrCents: IDL.Nat,
    signupsLast24h: IDL.Nat,
    signupsLast7d: IDL.Nat,
    signupsLast30d: IDL.Nat,
    // Trial visibility (additive) — mirrors the canister record exactly so
    // Candid does not drop these on decode. See SubscriptionMetrics interface.
    activeTrials: IDL.Nat,
    trialsStartedLast24h: IDL.Nat,
    trialsStartedLast7d: IDL.Nat,
    churnLast30d: IDL.Nat,
    failedPaymentsAwaitingRetry: IDL.Nat,
    refundsLast30d: IDL.Nat,
    disputesOpen: IDL.Nat,
  });

  const AggregatedMetricsIDL = IDL.Record({
    auth: IDL.Opt(AuthMetricsIDL),
    lobby: IDL.Opt(LobbyMetricsIDL),
    game: IDL.Opt(GameMetricsIDL),
    subscription: IDL.Opt(SubscriptionMetricsIDL),
    fetchedAt: IDL.Int,
  });

  const ResultOkNull = IDL.Variant({ ok: IDL.Null, err: IDL.Text });
  const ResultOkMetrics = IDL.Variant({ ok: AggregatedMetricsIDL, err: IDL.Text });
  const ResultOkHealth = IDL.Variant({ ok: IDL.Vec(CanisterRollupIDL), err: IDL.Text });
  const ResultOkTopUp = IDL.Variant({ ok: TopUpSummaryIDL, err: IDL.Text });
  const ResultOkAudit = IDL.Variant({ ok: IDL.Vec(AdminActionIDL), err: IDL.Text });
  const ResultOkNat = IDL.Variant({ ok: IDL.Nat, err: IDL.Text });

  // Mirrors canisters/admin/main.mo SubscriptionOverrideAction variant.
  const SubscriptionOverrideActionIDL = IDL.Variant({
    deactivate: IDL.Text,
    extend: IDL.Nat,
  });

  const AutoTopUpConfigIDL = IDL.Record({
    enabled: IDL.Bool,
    threshold: IDL.Nat,
    amount: IDL.Nat,
    dailyCap: IDL.Nat,
    dailyUsed: IDL.Nat,
    dailyWindowStart: IDL.Int,
    capRemaining: IDL.Nat,
  });

  // Data Rights IDL records — mirrors Koda's Candid types verbatim.
  const ExportedUserProfileIDL = IDL.Record({
    principal: IDL.Principal,
    displayName: IDL.Text,
    createdAt: IDL.Int,
    gamesPlayed: IDL.Nat,
    gamesWon: IDL.Nat,
  });

  const ExportedSubscriptionIDL = IDL.Record({
    isActive: IDL.Bool,
    activatedAt: IDL.Int,
    expiresAt: IDL.Int,
    stripeCustomerId: IDL.Text,
    trialStartedAt: IDL.Opt(IDL.Int),
    trialEndsAt: IDL.Opt(IDL.Int),
    seatNumber: IDL.Opt(IDL.Nat),
    isFoundingHand: IDL.Bool,
  });

  const PlayerStatsExportIDL = IDL.Record({
    schemaVersion: IDL.Nat,
    matchesPlayed: IDL.Nat, matchesWon: IDL.Nat,
    matchesWonByDominoOut: IDL.Nat, matchesWonByBlock: IDL.Nat,
    currentMatchStreak: IDL.Nat, bestMatchStreak: IDL.Nat,
    matchesPlayedDraw: IDL.Nat, matchesWonDraw: IDL.Nat,
    matchesPlayedStraight: IDL.Nat, matchesWonStraight: IDL.Nat,
    matchesPlayedByTarget100: IDL.Nat, matchesPlayedByTarget200: IDL.Nat, matchesPlayedByTarget300: IDL.Nat,
    matchesWonByTarget100: IDL.Nat, matchesWonByTarget200: IDL.Nat, matchesWonByTarget300: IDL.Nat,
    roundsPlayed: IDL.Nat, roundsWon: IDL.Nat,
    roundsWonByDominoOut: IDL.Nat, roundsWonByBlock: IDL.Nat, roundsBlocked: IDL.Nat,
    totalPointsScored: IDL.Nat, highestRoundScore: IDL.Nat, highestMatchScore: IDL.Nat,
    totalPointsScoredDraw: IDL.Nat, totalPointsScoredStraight: IDL.Nat,
    pointsScoredByDominoOut: IDL.Nat, pointsScoredByBlock: IDL.Nat,
    totalTilesPlayed: IDL.Nat, totalPasses: IDL.Nat, totalBoneyardDraws: IDL.Nat,
    dominoOuts: IDL.Nat, totalTilesRemainingOnLoss: IDL.Nat, roundsLostCount: IDL.Nat,
    totalPlayTimeMs: IDL.Int, totalTurnTimeMs: IDL.Int, turnsPlayed: IDL.Nat,
    fastestMatchWinMs: IDL.Int, longestMatchMs: IDL.Int,
    firstGameDate: IDL.Opt(IDL.Int), lastGameDate: IDL.Opt(IDL.Int),
    totalSessions: IDL.Nat,
    roundsWonWithoutPass: IDL.Nat, totalOpponentPipsOnWin: IDL.Nat,
    timedOutTurns: IDL.Nat, roundsClosedWithLastTile: IDL.Nat, longestOwnPlaySequence: IDL.Nat,
    migrated: IDL.Bool, lastMatchAt: IDL.Opt(IDL.Int),
  });

  const UserDataBundleIDL = IDL.Record({
    exportedAt: IDL.Int,
    exporterPrincipal: IDL.Principal,
    targetPrincipal: IDL.Principal,
    profile: IDL.Opt(ExportedUserProfileIDL),
    subscription: IDL.Opt(ExportedSubscriptionIDL),
    stats: IDL.Opt(PlayerStatsExportIDL),
  });

  const ResultOkBundle = IDL.Variant({ ok: UserDataBundleIDL, err: IDL.Text });
  const ResultOkOptPrincipal = IDL.Variant({ ok: IDL.Opt(IDL.Principal), err: IDL.Text });

  // Stale-rooms + cycles dashboard IDL records.
  // Mirror canisters/admin/main.mo:649-664 structural subtype exactly.
  const LobbyRoomStatusIDL = IDL.Variant({
    waiting: IDL.Null,
    full: IDL.Null,
    playing_open: IDL.Null,
    playing: IDL.Null,
    finished: IDL.Null,
  });
  const LobbyGameModeIDL = IDL.Variant({
    draw: IDL.Null,
    straight: IDL.Null,
  });
  const LobbyRoomVisibilityIDL = IDL.Variant({
    public_room: IDL.Null,
    private_room: IDL.Null,
  });
  const LobbyRoomInfoIDL = IDL.Record({
    roomId: IDL.Text,
    mode: LobbyGameModeIDL,
    target: IDL.Nat,
    hostPrincipal: IDL.Principal,
    hostName: IDL.Text,
    playerCount: IDL.Nat,
    maxPlayers: IDL.Nat,
    createdAt: IDL.Int,
    status: LobbyRoomStatusIDL,
    playerNames: IDL.Vec(IDL.Text),
    playerPrincipals: IDL.Vec(IDL.Principal),
    visibility: LobbyRoomVisibilityIDL,
    gameRoomId: IDL.Opt(IDL.Text),
    gameStartedAt: IDL.Opt(IDL.Int),
  });
  const ResultOkStaleRooms = IDL.Variant({
    ok: IDL.Vec(IDL.Tuple(IDL.Text, LobbyRoomInfoIDL)),
    err: IDL.Text,
  });
  const ResultOkCanisterCycles = IDL.Variant({
    ok: IDL.Vec(IDL.Tuple(IDL.Text, IDL.Nat)),
    err: IDL.Text,
  });
  const BackendPrincipalsIDL = IDL.Record({
    auth: IDL.Opt(IDL.Principal),
    lobby: IDL.Opt(IDL.Principal),
    game: IDL.Opt(IDL.Principal),
    subscription: IDL.Opt(IDL.Principal),
    cyclesWallet: IDL.Opt(IDL.Principal),
    stats: IDL.Opt(IDL.Principal),
    frontend: IDL.Opt(IDL.Principal),
  });

  // Session token IDL — Phase 3
  const SessionTokenIDL = IDL.Record({
    token: IDL.Text,
    expiresAt: IDL.Int,
  });
  const ResultOkSessionToken = IDL.Variant({ ok: SessionTokenIDL, err: IDL.Text });

  return IDL.Service({
    init_admin: IDL.Func([], [ResultOkNull], []),
    add_admin: IDL.Func([IDL.Principal], [ResultOkNull], []),
    remove_admin: IDL.Func([IDL.Principal], [ResultOkNull], []),
    get_admins: IDL.Func([], [IDL.Vec(IDL.Principal)], ['query']),
    is_admin: IDL.Func([IDL.Principal], [IDL.Bool], ['query']),
    admin_get_aggregated_metrics: IDL.Func([], [ResultOkMetrics], []),
    admin_get_all_canister_health: IDL.Func([], [ResultOkHealth], []),
    get_auto_top_up_config: IDL.Func([], [AutoTopUpConfigIDL], ['query']),
    auto_top_up_check: IDL.Func([], [ResultOkTopUp], []),
    set_auto_top_up_enabled: IDL.Func([IDL.Bool], [ResultOkNull], []),
    set_auto_top_up_threshold: IDL.Func([IDL.Nat], [ResultOkNull], []),
    set_auto_top_up_amount: IDL.Func([IDL.Nat], [ResultOkNull], []),
    set_daily_top_up_cap: IDL.Func([IDL.Nat], [ResultOkNull], []),
    admin_list_banned_words: IDL.Func([], [IDL.Vec(IDL.Text)], ['query']),
    admin_add_banned_word: IDL.Func([IDL.Text], [ResultOkNull], []),
    admin_remove_banned_word: IDL.Func([IDL.Text], [ResultOkNull], []),
    admin_set_banned_words: IDL.Func([IDL.Vec(IDL.Text)], [ResultOkNull], []),
    // Motoko: 3 positional args — actionType: ?Text, sinceTimestamp: ?Int, limit: Nat
    admin_get_audit_log_filtered: IDL.Func(
      [IDL.Opt(IDL.Text), IDL.Opt(IDL.Int), IDL.Nat],
      [ResultOkAudit],
      ['query'],
    ),
    admin_force_rename: IDL.Func([IDL.Principal, IDL.Text], [ResultOkNull], []),
    admin_force_finish_room: IDL.Func([IDL.Text], [ResultOkNull], []),
    admin_manual_top_up_canister: IDL.Func(
      [IDL.Principal, IDL.Nat],
      [ResultOkNat],
      [],
    ),
    admin_subscription_override: IDL.Func(
      [IDL.Principal, SubscriptionOverrideActionIDL],
      [ResultOkNull],
      [],
    ),
    // Session token — Phase 3 (2026-05-14)
    admin_issue_session_token: IDL.Func([], [ResultOkSessionToken], []),
    validate_session_token: IDL.Func([IDL.Text], [IDL.Opt(IDL.Principal)], ['query']),
    admin_revoke_session_token: IDL.Func([IDL.Text], [ResultOkNull], []),

    // Data Rights — Block 3 (2026-05-14)
    admin_export_user_data: IDL.Func([IDL.Principal], [ResultOkBundle], []),
    admin_lookup_principal_by_stripe_customer: IDL.Func([IDL.Text], [ResultOkOptPrincipal], []),
    admin_log_privacy_event: IDL.Func(
      [IDL.Text, IDL.Opt(IDL.Text), IDL.Text],
      [ResultOkNull],
      [],
    ),

    // Stale-rooms + cycles dashboard — D2 / D3 (2026-05-11 canister, wired 2026-05-22)
    list_stale_rooms: IDL.Func([IDL.Nat], [ResultOkStaleRooms], []),
    get_all_canister_cycles: IDL.Func([], [ResultOkCanisterCycles], []),
    get_backend_principals: IDL.Func([], [BackendPrincipalsIDL], ['query']),

    // ICP -> Cycles top-up via CMC (Atlas, 2026-05-22).
    get_icp_topup_account_id: IDL.Func([], [IDL.Text], ['query']),
    notify_icp_topup: IDL.Func([IDL.Nat64], [ResultOkNat], []),
  });
};

// ---------------------------------------------------------------------------
// Environment detection (mirrors icpAgent.ts pattern)
// ---------------------------------------------------------------------------

const isDev = import.meta.env.VITE_DFX_NETWORK !== 'ic';
const LOCAL_HOST = import.meta.env.VITE_ICP_HOST || 'http://localhost:4947';
const IC_HOST = 'https://icp0.io';

function getAdminCanisterId(): string {
  const id = import.meta.env.VITE_ADMIN_CANISTER_ID;
  if (!id || typeof id !== 'string' || id.trim() === '') {
    if (isDev) {
      console.warn(
        '[adminAgent] VITE_ADMIN_CANISTER_ID not set. ' +
        'Run dfx deploy admin_canister to obtain the canister ID.'
      );
      return '';
    }
    throw new Error('[adminAgent] Missing VITE_ADMIN_CANISTER_ID environment variable.');
  }
  return id.trim();
}

// ---------------------------------------------------------------------------
// Agent + actor factory
// ---------------------------------------------------------------------------

/**
 * Create an HttpAgent for the admin canister.
 * fetchRootKey() is ONLY called in dev mode. (Anti-Hallucination rule #10)
 */
export async function createAdminAgent(identity?: Identity): Promise<HttpAgent> {
  const agent = new HttpAgent({
    host: isDev ? LOCAL_HOST : IC_HOST,
    identity,
  });

  if (isDev) {
    // fetchRootKey is local-only. NEVER call in production. (CLAUDE.md #10)
    await agent.fetchRootKey();
  }

  return agent;
}

/**
 * Create a typed actor for the dominus_admin canister.
 * Falls back to a mock actor in dev mode when the canister is not yet deployed.
 */
export function createAdminActor(agent: HttpAgent): ActorSubclass<AdminService> {
  const canisterId = getAdminCanisterId();
  if (!canisterId) {
    return createMockAdminActor();
  }
  return Actor.createActor<AdminService>(adminIdlFactory, {
    agent,
    canisterId,
  });
}

// ---------------------------------------------------------------------------
// Mock actor for dev mode (canister not yet deployed)
// ---------------------------------------------------------------------------

function createMockAdminActor(): ActorSubclass<AdminService> {
  // No-op stub that keeps the UI functional during Phase A development
  // before the admin_canister is deployed. All methods return empty/ok.
  return {
    init_admin: async () => ({ ok: null }),
    add_admin: async () => ({ ok: null }),
    remove_admin: async () => ({ ok: null }),
    get_admins: async () => [],
    is_admin: async () => false,
    admin_get_aggregated_metrics: async () => ({
      ok: {
        auth: [],
        lobby: [],
        game: [],
        subscription: [],
        fetchedAt: BigInt(Date.now() * 1_000_000),
      },
    }),
    admin_get_all_canister_health: async () => ({ ok: [] }),
    get_auto_top_up_config: async () => ({
      enabled: true,
      threshold: 1_000_000_000_000n,
      amount: 2_000_000_000_000n,
      dailyCap: 10_000_000_000_000n,
      dailyUsed: 0n,
      dailyWindowStart: 0n,
      capRemaining: 10_000_000_000_000n,
    }),
    auto_top_up_check: async () => ({
      ok: {
        triggered: 0n,
        cyclesDeposited: 0n,
        capRemaining: 0n,
        skipped: [],
        timestamp: BigInt(Date.now() * 1_000_000),
      },
    }),
    set_auto_top_up_enabled: async () => ({ ok: null }),
    set_auto_top_up_threshold: async () => ({ ok: null }),
    set_auto_top_up_amount: async () => ({ ok: null }),
    set_daily_top_up_cap: async () => ({ ok: null }),
    admin_list_banned_words: async () => [],
    admin_add_banned_word: async () => ({ ok: null }),
    admin_remove_banned_word: async () => ({ ok: null }),
    admin_set_banned_words: async () => ({ ok: null }),
    admin_get_audit_log_filtered: async () => ({ ok: [] }),
    admin_force_rename: async () => ({ ok: null }),
    admin_force_finish_room: async () => ({ ok: null }),
    admin_manual_top_up_canister: async () => ({ ok: 0n }),
    admin_subscription_override: async () => ({ ok: null }),
    // Session token mock stubs — DEV_BYPASS path returns a 64-char hex mock token
    // valid for 4 hours. Mirrors the canister's ~4h expiry window.
    admin_issue_session_token: async () => ({
      ok: {
        token: 'mock-token-0000000000000000000000000000000000000000000000000000000000000000',
        expiresAt: BigInt(Date.now() * 1_000_000 + 4 * 60 * 60 * 1_000_000_000),
      },
    }),
    validate_session_token: async () => [],
    admin_revoke_session_token: async () => ({ ok: null }),
    // Data Rights mock stubs — DEV_BYPASS path returns plausible empty bundles.
    admin_export_user_data: async () => ({
      ok: {
        exportedAt: BigInt(Date.now() * 1_000_000),
        exporterPrincipal: { toText: () => 'mock-exporter' } as unknown as Principal,
        targetPrincipal: { toText: () => 'mock-target' } as unknown as Principal,
        profile: [],
        subscription: [],
        stats: [],
      },
    }),
    admin_lookup_principal_by_stripe_customer: async () => ({ ok: [] }),
    admin_log_privacy_event: async () => ({ ok: null }),
    // Stale-rooms + cycles dashboard mock stubs — empty arrays so the
    // panels render the "No stale rooms. The lobby is clear." empty state
    // during dev with the canister unwired.
    list_stale_rooms: async () => ({ ok: [] }),
    get_all_canister_cycles: async () => ({ ok: [] }),
    get_backend_principals: async () => ({
      auth: [], lobby: [], game: [], subscription: [],
      cyclesWallet: [], stats: [], frontend: [],
    }),
    // ICP -> Cycles top-up mock stubs (Atlas, 2026-05-22). The account ID is
    // a Jerry-anonymous principal-derived placeholder; the notify mock
    // returns a typed err so the dev UI surfaces the "no real ICP transfer"
    // failure mode without hitting CMC.
    get_icp_topup_account_id: async () =>
      '0000000000000000000000000000000000000000000000000000000000000000',
    notify_icp_topup: async () => ({
      err: 'DEV_BYPASS: notify_icp_topup is mocked. Deploy to local replica to test against CMC stub.',
    }),
  } as unknown as ActorSubclass<AdminService>;
}

// ---------------------------------------------------------------------------
// Lobby canister actor — minimal IDL for the one method useStaleRooms needs:
// admin_force_finish_room. The Motoko method lives on lobby_canister
// (canisters/lobby/main.mo:2055), NOT on dominus_admin. It admin-gates on the
// lobby's own allowlist (which init-admin.sh registers identically to
// dominus_admin), and wraps admin_force_close_room which deletes the room
// + clears the playerRoomIndex.
//
// Routing the force-close through lobby directly (rather than a passthrough
// on dominus_admin) is the lowest-diff option per Jerry's Q3 directive
// 2026-05-22 -- no new canister method, no breaking IDL deploy.
// ---------------------------------------------------------------------------

export interface LobbyService {
  admin_force_finish_room: (roomId: string) => Promise<AdminResult<null>>;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const lobbyIdlFactory: InterfaceFactory = ({ IDL }: any) => {
  const ResultOkNull = IDL.Variant({ ok: IDL.Null, err: IDL.Text });
  return IDL.Service({
    admin_force_finish_room: IDL.Func([IDL.Text], [ResultOkNull], []),
  });
};

function getLobbyCanisterId(): string {
  const id = import.meta.env.VITE_LOBBY_CANISTER_ID;
  if (!id || typeof id !== 'string' || id.trim() === '') {
    if (isDev) {
      console.warn('[adminAgent] VITE_LOBBY_CANISTER_ID not set.');
      return '';
    }
    throw new Error('[adminAgent] Missing VITE_LOBBY_CANISTER_ID environment variable.');
  }
  return id.trim();
}

/// Create a typed actor for lobby_canister scoped to the one admin method
/// useStaleRooms needs. Returns a mock actor when the canister ID is not
/// configured (dev path with no replica).
export function createLobbyAdminActor(agent: HttpAgent): ActorSubclass<LobbyService> {
  const canisterId = getLobbyCanisterId();
  if (!canisterId) {
    return {
      admin_force_finish_room: async () => ({ ok: null }),
    } as unknown as ActorSubclass<LobbyService>;
  }
  return Actor.createActor<LobbyService>(lobbyIdlFactory, {
    agent,
    canisterId,
  });
}
